import { Suspense, lazy, type ComponentType } from 'react';
import { Router, Route, Switch, Redirect } from 'wouter';
import { useTranslation } from 'react-i18next';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from '@/contexts/auth-context';
import { SubscriptionProvider } from '@/contexts/subscription-context';
import { Toaster } from '@/components/ui/toaster';
import { ScrollToTop } from '@/components/ScrollToTop';
import { isSubdomainSite } from '@/utils/subdomain';
import './i18n/config';

// Pages
import HomePage from '@/pages/home';
import LoginPage from '@/pages/auth/login';
import GoogleCallbackPage from '@/pages/auth/google-callback';
import RegisterOrganizationPage from '@/pages/register';
import OnboardingPage from '@/pages/admin/onboarding';

// Admin Pages
import AdminDashboardPage from '@/pages/admin/dashboard';
import AdminBookingsPage from '@/pages/admin/bookings';
import AdminBookingDetailsPage from '@/pages/admin/booking-details';
import AdminGroupClassPage from '@/pages/admin/group-class';
import NewBookingPage from '@/pages/admin/NewBookingPage';
import NewRecurringBookingPage from '@/pages/admin/NewRecurringBookingPage';
import NewGroupRecurringBookingPage from '@/pages/admin/NewGroupRecurringBookingPage';
import AdminServicesPage from '@/pages/admin/services';
import AdminTeamPage from '@/pages/admin/team';
import AdminTeamDetailPage from '@/pages/admin/team-detail';
import AdminSlotsPage from '@/pages/admin/slots';
import AdminAvailabilityPage from '@/pages/admin/availability';
import AdminSettingsPage from '@/pages/admin/settings';
import AdminUsersPage from '@/pages/admin/users';
import CalendarSettingsPage from '@/pages/admin/calendar-settings';
import CalendarCallbackPage from '@/pages/admin/calendar-callback';
import BillingPage from '@/pages/admin/billing';
import AdminOrganizationPage from '@/pages/admin/organization';
import AdminReportsPage from '@/pages/admin/reports';
import AdminCustomersPage from '@/pages/admin/customers';
import AdminCustomerDetailPage from '@/pages/admin/customer-detail';
// Sloty Sessions: lazy, only therapy practices use it (and the editor pulls in Tiptap).
const SessionNoteEditorPage = lazy(() => import('@/pages/admin/sessions/note-editor'));
const SessionsOverviewPage = lazy(() => import('@/pages/admin/sessions'));
const SessionClientRecordPage = lazy(() => import('@/pages/admin/sessions/client-record'));
import { TherapyPracticeGuard } from '@/components/sessions/TherapyPracticeGuard';
import CustomQuestionsPage from '@/pages/admin/custom-questions';
import AdminTipsPage from '@/pages/admin/tips';

// Staff Pages
import StaffDashboardPage from '@/pages/staff/dashboard';
import StaffBookingsPage from '@/pages/staff/bookings';
import StaffBookingDetailsPage from '@/pages/staff/booking-details';
import StaffGroupClassPage from '@/pages/staff/group-class';
import StaffSchedulePage from '@/pages/staff/schedule';
import StaffSettingsPage from '@/pages/staff/settings';

// Public Pages
import PublicBookingPage, { EmbeddedBookingPage } from '@/pages/public/book';
import AdminBookingPagePage from '@/pages/admin/booking-page';

// Guest Pages (no auth required)
import MyBookingsRequestPage from '@/pages/my-bookings';
import CustomerBookingsPage from '@/pages/customer-bookings';
import GuestBookingDetailsPage from '@/pages/guest/booking-details';
import GuestBookingCancelPage from '@/pages/guest/booking-cancel';
import GuestBookingReschedulePage from '@/pages/guest/booking-reschedule';
import GuestWaitlistPage from '@/pages/guest/waitlist';

// Profile & Auth Pages
import ChangeEmailPage from '@/pages/change-email';
import ChangePasswordPage from '@/pages/change-password';
import ForgotPasswordPage from '@/pages/forgot-password';
import ResetPasswordPage from '@/pages/reset-password';
import AcceptInvitationPage from '@/pages/accept-invitation';
import ConfirmEmailPage from '@/pages/confirm-email';

// Pricing & Billing Pages
import PricingPage from '@/pages/pricing';
import CheckoutPage from '@/pages/billing/checkout';

// Landing Pages
import SalonsLandingPage from '@/pages/landing/salons';
import DoctorsLandingPage from '@/pages/landing/doctors';
import GolfTeachersLandingPage from '@/pages/landing/golf-teachers';
import BeautyLandingPage from '@/pages/landing/beauty';
import SpaLandingPage from '@/pages/landing/spa';
import TrainersLandingPage from '@/pages/landing/trainers';
import CoachesLandingPage from '@/pages/landing/coaches';
import BarbersLandingPage from '@/pages/landing/barbers';
import ClinicsLandingPage from '@/pages/landing/clinics';
import FeaturesPage from '@/pages/landing/features';
import CalendlyComparisonPage from '@/pages/landing/compare/calendly';
import AcuityComparisonPage from '@/pages/landing/compare/acuity';
import SimplyBookComparisonPage from '@/pages/landing/compare/simplybook';
import AboutPage from '@/pages/about';

// Blog Pages
import BlogIndex from '@/pages/blog';
import BlogPostPage from '@/pages/blog/BlogPost';

// Legal Pages
import PrivacyPolicyPage from '@/pages/legal/privacy';
import TermsOfServicePage from '@/pages/legal/terms';
import CookiePolicyPage from '@/pages/legal/cookies';
import ImpressumPage from '@/pages/legal/impressum';

// Cookie Consent (lazy-loaded to survive ad blockers blocking react-cookie-consent)
const CookieConsentBanner = lazy(() =>
  import('@/components/CookieConsentBanner')
    .then(m => ({ default: m.CookieConsentBanner }))
    .catch(() => ({ default: () => null }))
);

// Invite-only therapists landing — lazy: flag-gated campaign page, kept out of the main bundle.
import { InviteLanding } from '@/pages/landing/InviteLanding';
const TherapistsInvitePage = lazy(() => import('@/pages/landing/therapists-invite'));
const ForTherapistsPage = lazy(() =>
  import('@/pages/landing/therapists-invite').then((m) => ({ default: m.ForTherapistsPage }))
);
// Small gyms & studios: public page (indexed) and the flag-gated invite variant share one component.
const SmallGymsPage = lazy(() => import('@/pages/landing/small-gyms'));
const SmallGymsInvitePage = lazy(() =>
  import('@/pages/landing/small-gyms').then((m) => ({ default: m.SmallGymsInvitePage }))
);
import { isInviteLandingEnabled } from '@/lib/featureFlags';
import { LocalizedLanding } from '@/pages/landing/LocalizedLanding';
import { LOCALIZED_LANDINGS, type LandingId, type LandingLang } from '@/lib/marketing/localizedLandings';

const LANDING_COMPONENTS: Record<LandingId, ComponentType> = {
  salons: SalonsLandingPage,
  barbers: BarbersLandingPage,
  beauty: BeautyLandingPage,
  spa: SpaLandingPage,
  coaches: CoachesLandingPage,
  'golf-teachers': GolfTeachersLandingPage,
  doctors: DoctorsLandingPage,
  clinics: ClinicsLandingPage,
  trainers: TrainersLandingPage,
  'small-gyms': SmallGymsPage,
  therapists: ForTherapistsPage,
};

import UnauthorizedPage from '@/pages/unauthorized';
import NotFoundPage from '@/pages/not-found';

// Platform (super-admin) Pages
import PlatformLoginPage from '@/pages/platform/login';
import PlatformUsersPage from '@/pages/platform/users';
import PlatformDashboardPage from '@/pages/platform/dashboard';
import PlatformInvitationCodesPage from '@/pages/platform/invitation-codes';
import PlatformInvitationCodeDetailPage from '@/pages/platform/invitation-code-detail';
import { ImpersonationBanner } from '@/components/ImpersonationBanner';

// Layouts
import { MainLayout } from '@/layouts/main-layout';
import { AdminLayout } from '@/layouts/admin-layout';

// Protected Route Component
import { ProtectedRoute } from '@/components/auth/protected-route';

function LoadingFallback() {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-center min-h-screen">
      {t('common:loading')}
    </div>
  );
}

const APP_VERSION = __APP_VERSION__;

// Layout wrapper that conditionally uses AdminLayout for admin/staff, MainLayout for customers
function ProfileLayoutWrapper({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();

  // Use AdminLayout for admin and staff users (they have sidebar navigation)
  if (user?.role === 'admin' || user?.role === 'staff') {
    return <AdminLayout>{children}</AdminLayout>;
  }

  // Use MainLayout for customer users
  return <MainLayout>{children}</MainLayout>;
}

function App() {
  return (
    <HelmetProvider>
      <Suspense fallback={<LoadingFallback />}>
        <AuthProvider>
          <SubscriptionProvider>
            <Router>
              <ScrollToTop />
              {/* Global impersonation banner (shows across all layouts) */}
              <ImpersonationBanner />
              {/* Hidden version indicator for deployment verification */}
              <p style={{ display: 'none' }} data-app-version={APP_VERSION}>v{APP_VERSION}</p>
              <Switch>
                {/* Public routes */}
                <Route path="/">
                  {() => {
                    // Show booking page directly for subdomains (e.g., rodrigo-clinic.slotyapp.com)
                    if (isSubdomainSite()) {
                      return <PublicBookingPage />;
                    }
                    return <HomePage />;
                  }}
                </Route>
                <Route path="/login" component={LoginPage} />
                <Route path="/register" component={RegisterOrganizationPage} />

                {/* Platform super-admin console (separate PlatformAdmin identity) */}
                <Route path="/platform" component={PlatformDashboardPage} />
                <Route path="/platform/login" component={PlatformLoginPage} />
                <Route path="/platform/users" component={PlatformUsersPage} />
                <Route path="/platform/invitation-codes" component={PlatformInvitationCodesPage} />
                <Route path="/platform/invitation-codes/:id" component={PlatformInvitationCodeDetailPage} />
                {/* Was a "Coming Soon" stub; kept as a redirect since it may
                    still be linked or bookmarked. /register is the real page. */}
                <Route path="/auth/register"><Redirect to="/register" /></Route>
                <Route path="/auth/google/callback" component={GoogleCallbackPage} />
                {/* Step 0 for Google signups: no account exists until this is submitted */}
                <Route path="/unauthorized" component={UnauthorizedPage} />

                {/* Public pricing page */}
                <Route path="/pricing" component={PricingPage} />

                {/* Landing pages */}
                {/* One route per language version (localizedLandings.ts): /for-salons, /de/fuer-friseursalons, … */}
                {(Object.keys(LOCALIZED_LANDINGS) as LandingId[]).flatMap((id) =>
                  Object.entries(LOCALIZED_LANDINGS[id]).map(([lang, path]) => {
                    const Page = LANDING_COMPONENTS[id];
                    return (
                      <Route key={path} path={path}>
                        <LocalizedLanding id={id} lang={lang as LandingLang}>
                          <Page />
                        </LocalizedLanding>
                      </Route>
                    );
                  }),
                )}
                {/* Invite campaigns get one URL per language. A link-preview crawler
                    sends no usable Accept-Language, so a single address could only ever
                    carry one card; a per-language URL also keeps a German therapist on
                    the German page whatever their browser reports. */}
                {isInviteLandingEnabled && (
                  <Route path="/invite/therapists">
                    <InviteLanding lang="en"><TherapistsInvitePage /></InviteLanding>
                  </Route>
                )}
                {isInviteLandingEnabled && (
                  <Route path="/de/invite/therapists">
                    <InviteLanding lang="de"><TherapistsInvitePage /></InviteLanding>
                  </Route>
                )}
                {isInviteLandingEnabled && (
                  <Route path="/pt-br/invite/therapists">
                    <InviteLanding lang="pt-BR"><TherapistsInvitePage /></InviteLanding>
                  </Route>
                )}
                {isInviteLandingEnabled && (
                  <Route path="/invite/gyms">
                    <InviteLanding lang="en"><SmallGymsInvitePage /></InviteLanding>
                  </Route>
                )}
                {isInviteLandingEnabled && (
                  <Route path="/de/invite/gyms">
                    <InviteLanding lang="de"><SmallGymsInvitePage /></InviteLanding>
                  </Route>
                )}
                <Route path="/features" component={FeaturesPage} />
                <Route path="/compare/calendly" component={CalendlyComparisonPage} />
                <Route path="/compare/acuity" component={AcuityComparisonPage} />
                <Route path="/compare/simplybook" component={SimplyBookComparisonPage} />
                <Route path="/about" component={AboutPage} />

                {/* Blog */}
                <Route path="/blog" component={BlogIndex} />
                <Route path="/blog/:slug" component={BlogPostPage} />

                {/* Legal pages */}
                <Route path="/privacy" component={PrivacyPolicyPage} />
                <Route path="/terms" component={TermsOfServicePage} />
                <Route path="/cookies" component={CookiePolicyPage} />
                {/* Austria/Germany require this disclosure to be reachable directly
                    from every page (§5 ECG, §5 DDG); /imprint is an English alias. */}
                <Route path="/impressum" component={ImpressumPage} />
                <Route path="/imprint" component={ImpressumPage} />

                {/* Public auth pages */}
                <Route path="/forgot-password" component={ForgotPasswordPage} />
                <Route path="/reset-password/:token" component={ResetPasswordPage} />
                <Route path="/accept-invitation/:token" component={AcceptInvitationPage} />
                <Route path="/confirm-email/:userId/:token" component={ConfirmEmailPage} />

                {/* Customer "My bookings" magic link (no auth required) */}
                <Route path="/my-bookings" component={MyBookingsRequestPage} />
                <Route path="/bookings/:token" component={CustomerBookingsPage} />

                {/* Public booking pages */}
                <Route path="/book" component={PublicBookingPage} />  {/* Subdomain-based */}
                <Route path="/:slug/book" component={PublicBookingPage} />  {/* Slug-based */}
                <Route path="/embed" component={EmbeddedBookingPage} />  {/* Embed, subdomain-based */}
                <Route path="/:slug/embed" component={EmbeddedBookingPage} />  {/* Embed, slug-based */}

                {/* Guest booking management (no auth required - token-based) */}
                <Route path="/guest/bookings/:token" component={GuestBookingDetailsPage} />
                <Route path="/guest/bookings/:token/cancel" component={GuestBookingCancelPage} />
                <Route path="/guest/bookings/:token/reschedule" component={GuestBookingReschedulePage} />
                <Route path="/guest/waitlist/:token" component={GuestWaitlistPage} />

                {/* Staff booking details - must be BEFORE /staff/:rest* */}
                <Route path="/staff/bookings/:id">
                  <ProtectedRoute requiredRole="staff">
                    <AdminLayout>
                      <StaffBookingDetailsPage />
                    </AdminLayout>
                  </ProtectedRoute>
                </Route>

                {/* Staff group-class details - must be BEFORE /staff/:rest* */}
                <Route path="/staff/group-classes/:id">
                  <ProtectedRoute requiredRole="staff">
                    <AdminLayout>
                      <StaffGroupClassPage />
                    </AdminLayout>
                  </ProtectedRoute>
                </Route>

                {/* Staff routes */}
                <Route path="/staff/:rest*">
                  <ProtectedRoute requiredRole="staff">
                    <AdminLayout>
                      <Switch>
                        <Route path="/staff/dashboard" component={StaffDashboardPage} />
                        <Route path="/staff/bookings" component={StaffBookingsPage} />
                        <Route path="/staff/schedule" component={StaffSchedulePage} />
                        <Route path="/staff/settings" component={StaffSettingsPage} />
                        <Route path="/staff/calendar-settings" component={CalendarSettingsPage} />
                        <Route path="/staff/calendar-callback" component={CalendarCallbackPage} />
                        {/* Redirect old availability URL to settings */}
                        <Route path="/staff/availability" component={() => <Redirect to="/staff/settings" />} />
                        <Route path="/staff" component={() => <Redirect to="/staff/dashboard" />} />
                      </Switch>
                    </AdminLayout>
                  </ProtectedRoute>
                </Route>

                {/* Legacy staff route - redirect to new location */}
                <Route path="/dashboard">
                  <ProtectedRoute requiredRole="staff">
                    <Redirect to="/staff/dashboard" />
                  </ProtectedRoute>
                </Route>

                {/* Billing checkout - protected route */}
                <Route path="/billing/checkout">
                  <ProtectedRoute requiredRole="admin">
                    <CheckoutPage />
                  </ProtectedRoute>
                </Route>

                {/* Admin new booking - must be BEFORE /admin/:rest* */}
                <Route path="/admin/bookings/new/group-recurring">
                  <ProtectedRoute requiredRole="admin">
                    <AdminLayout>
                      <NewGroupRecurringBookingPage />
                    </AdminLayout>
                  </ProtectedRoute>
                </Route>
                <Route path="/admin/bookings/new/recurring">
                  <ProtectedRoute requiredRole="admin">
                    <AdminLayout>
                      <NewRecurringBookingPage />
                    </AdminLayout>
                  </ProtectedRoute>
                </Route>
                <Route path="/admin/bookings/new">
                  <ProtectedRoute requiredRole="admin">
                    <AdminLayout>
                      <NewBookingPage />
                    </AdminLayout>
                  </ProtectedRoute>
                </Route>

                {/* Admin booking details - must be BEFORE /admin/:rest* */}
                <Route path="/admin/bookings/:id">
                  <ProtectedRoute requiredRole="admin">
                    <AdminLayout>
                      <AdminBookingDetailsPage />
                    </AdminLayout>
                  </ProtectedRoute>
                </Route>

                {/* Admin group-class details - must be BEFORE /admin/:rest* */}
                <Route path="/admin/group-classes/:id">
                  <ProtectedRoute requiredRole="admin">
                    <AdminLayout>
                      <AdminGroupClassPage />
                    </AdminLayout>
                  </ProtectedRoute>
                </Route>

                {/* Customer detail - admins + staff. Must be BEFORE /admin/:rest* (which is admin-only). */}
                <Route path="/admin/customers/:id">
                  <ProtectedRoute requiredRole="staff">
                    <AdminLayout>
                      <AdminCustomerDetailPage />
                    </AdminLayout>
                  </ProtectedRoute>
                </Route>

                {/* Team detail - admin only. Must be BEFORE /admin/:rest* (which routes /admin/team to the index page). */}
                <Route path="/admin/team/:id">
                  <ProtectedRoute requiredRole="admin">
                    <AdminLayout>
                      <AdminTeamDetailPage />
                    </AdminLayout>
                  </ProtectedRoute>
                </Route>

                {/* Sloty Sessions note editor - admins + staff who are providers in a psychotherapy
                    practice (TherapyPracticeGuard). Sessions has no menu item or pages of its own:
                    notes are reached from the customer page and the booking. Must be BEFORE /admin/:rest*. */}
                <Route path="/admin/sessions/notes/:id">
                  <ProtectedRoute requiredRole="staff">
                    <AdminLayout>
                      <TherapyPracticeGuard><SessionNoteEditorPage /></TherapyPracticeGuard>
                    </AdminLayout>
                  </ProtectedRoute>
                </Route>
                <Route path="/admin/sessions/clients/:customerId">
                  <ProtectedRoute requiredRole="staff">
                    <AdminLayout>
                      <TherapyPracticeGuard><SessionClientRecordPage /></TherapyPracticeGuard>
                    </AdminLayout>
                  </ProtectedRoute>
                </Route>
                <Route path="/admin/sessions">
                  <ProtectedRoute requiredRole="staff">
                    <AdminLayout>
                      <TherapyPracticeGuard><SessionsOverviewPage /></TherapyPracticeGuard>
                    </AdminLayout>
                  </ProtectedRoute>
                </Route>

                {/* Customer list - admins + staff. Must be BEFORE /admin/:rest*. */}
                <Route path="/admin/customers">
                  <ProtectedRoute requiredRole="staff">
                    <AdminLayout>
                      <AdminCustomersPage />
                    </AdminLayout>
                  </ProtectedRoute>
                </Route>

                {/* Admin routes */}
                <Route path="/admin/:rest*">
                  <ProtectedRoute requiredRole="admin">
                    <AdminLayout>
                      <Switch>
                        <Route path="/admin/onboarding" component={OnboardingPage} />
                        <Route path="/admin/dashboard" component={AdminDashboardPage} />
                        <Route path="/admin/bookings" component={AdminBookingsPage} />
                        <Route path="/admin/services" component={AdminServicesPage} />
                        <Route path="/admin/team" component={AdminTeamPage} />
                        <Route path="/admin/users" component={AdminUsersPage} />
                        <Route path="/admin/slots" component={AdminSlotsPage} />
                        <Route path="/admin/time-slots">
                          <Redirect to="/admin/slots" />
                        </Route>
                        <Route path="/admin/availability" component={AdminAvailabilityPage} />
                        <Route path="/admin/settings" component={AdminSettingsPage} />
                        <Route path="/admin/booking-page" component={AdminBookingPagePage} />
                        <Route path="/admin/calendar-settings" component={CalendarSettingsPage} />
                        <Route path="/admin/custom-questions" component={CustomQuestionsPage} />
                        <Route path="/admin/organization" component={AdminOrganizationPage} />
                        <Route path="/admin/reports" component={AdminReportsPage} />
                        <Route path="/admin/billing" component={BillingPage} />
                        <Route path="/admin/tips" component={AdminTipsPage} />
                        <Route path="/admin" component={() => <Redirect to="/admin/dashboard" />} />
                      </Switch>
                    </AdminLayout>
                  </ProtectedRoute>
                </Route>

                {/* Email change (all authenticated users) - uses AdminLayout for admin/staff */}
                <Route path="/change-email">
                  <ProtectedRoute>
                    <ProfileLayoutWrapper>
                      <Route path="/change-email" component={ChangeEmailPage} />
                    </ProfileLayoutWrapper>
                  </ProtectedRoute>
                </Route>

                {/* Password change (all authenticated users) - uses AdminLayout for admin/staff */}
                <Route path="/change-password">
                  <ProtectedRoute>
                    <ProfileLayoutWrapper>
                      <Route path="/change-password" component={ChangePasswordPage} />
                    </ProfileLayoutWrapper>
                  </ProtectedRoute>
                </Route>

                {/* 404 */}
                <Route component={NotFoundPage} />
              </Switch>
            </Router>
            <Toaster />
            <CookieConsentBanner />
          </SubscriptionProvider>
        </AuthProvider>
      </Suspense>
    </HelmetProvider>
  );
}

export default App;